import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { setToken, api } from '../services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Mail, Lock } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegisterEnabled, setIsRegisterEnabled] = useState(true);
  const { setUser } = useContext(AuthContext);
  const { t } = useTranslation();

  useEffect(() => {
    api<{ registrationEnabled: boolean }>('/config')
      .then((d) => setIsRegisterEnabled(d.registrationEnabled ?? true))
      .catch(() => setIsRegisterEnabled(true));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      if (data.accessToken) setToken(data.accessToken);
      setUser(data.user);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
      <div className="mb-8 flex items-center justify-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
      </div>
      <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)]">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-center text-2xl font-bold tracking-tight">Jani</CardTitle>
          <CardDescription className="text-center">Log in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Badge variant="destructive" className="w-full justify-center py-1.5 text-xs font-medium rounded-md">
                {error}
              </Badge>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10" placeholder="name@company.com" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10" placeholder="••••••••" required />
              </div>
            </div>
            <Button data-testid="login-button" type="submit" disabled={loading} loading={loading} className="w-full">{t('login')}</Button>
            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password" className="text-[var(--color-primary)] hover:underline">Forgot password?</Link>
              {isRegisterEnabled && (
                <Link to="/register" className="text-[var(--color-primary)] hover:underline">Create account</Link>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
